import React from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusCircleIcon,
  ArrowsRightLeftIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  CakeIcon,
  ExclamationTriangleIcon,
  UserIcon,
  PlayIcon,
  PauseIcon,
  ChartPieIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';

// Add CSS for custom scrollbar
const scrollbarStyles = `
  /* Custom scrollbar styles */
  .hide-scrollbar::-webkit-scrollbar {
    height: 4px;
    width: 4px;
  }

  .hide-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .hide-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
  }

  .hide-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
`;

export default function ProjectDailyTasksHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-indigo-300 via-sky-100 to-white w-full">
      {/* Add custom scrollbar styles */}
      <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ClockIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Daily Task Timeline</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Interactive daily task management system with real-time scheduling, progress tracking,
            and staff assignment capabilities for efficient project execution.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Daily Task Management Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <CalendarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Daily Scheduling</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Organize and schedule tasks throughout the day with precise time slots
                  from 6 AM to 11 PM for optimal workflow management.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <ChartPieIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Progress Tracking</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Real-time progress monitoring with visual indicators, completion statistics,
                  and performance metrics for each working day.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <ArrowsRightLeftIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Task Rearrangement</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Flexible task scheduling with drag-and-drop functionality and conflict
                  resolution for optimal staff utilization.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <UserIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Staff Assignment</h3>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Assign tasks to specific staff members with role-based permissions
                  and real-time availability tracking.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interface Button Layouts */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interface Button Layouts</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Header Navigation */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Navigation & Controls</h3>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Left side buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                      <PlusCircleIcon className="h-5 w-5" />
                      Add Task
                    </button>

                    <button className="inline-flex items-center gap-x-1.5 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                      <ChevronLeftIcon className="h-5 w-5" />
                      Previous
                    </button>

                    <button className="inline-flex items-center gap-x-1.5 rounded-md bg-emerald-500 px-3 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                      Next
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Right side - Pie chart placeholder */}
                  <div className="flex items-center gap-4">
                    <div className="text-xs font-semibold flex flex-wrap gap-2">
                      <div className="flex items-center space-x-1">
                        <span className="inline-block w-3 h-3 rounded-full bg-blue-500"></span>
                        <span>Completed</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="inline-block w-3 h-3 rounded-full bg-red-500"></span>
                        <span>Pending</span>
                      </div>
                    </div>
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                      <ChartPieIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Date Header */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Date & Time Header</h3>

              <div className="bg-white shadow rounded-lg p-4 border border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <h1 className="text-xl font-bold text-indigo-600">
                    Monday, Oct 14, 2025
                  </h1>

                  <div className="flex flex-wrap gap-2 items-center">
                    <button className="inline-flex items-center gap-x-1.5 rounded-md bg-yellow-500 px-3 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                      <CakeIcon className="h-5 w-5" />
                      Add Break
                    </button>

                    <div className="flex items-center space-x-1">
                      <button className="rounded-md bg-gray-200 p-2 text-gray-800 opacity-75 cursor-not-allowed">
                        <MagnifyingGlassMinusIcon className="h-5 w-5" />
                      </button>
                      <button className="rounded-md bg-gray-200 p-2 text-gray-800 opacity-75 cursor-not-allowed">
                        <MagnifyingGlassPlusIcon className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="flex items-center p-2 bg-violet-600 rounded-lg">
                      <ClockIcon className="h-5 w-5 text-white" />
                      <span className="text-lg text-white ml-1 font-bebas">
                        14:30
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Management Controls</h3>

              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 space-x-4">
                  <button className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    Bottlenecks
                  </button>

                  <button className="inline-flex items-center gap-x-1.5 rounded-md bg-purple-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                    <ArrowsRightLeftIcon className="h-5 w-5" />
                    Rearrange
                  </button>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden relative flex-1">
                    <div className="flex h-full">
                      <div className="bg-emerald-500 transition-all duration-300 ease-out" style={{ width: '60%' }} />
                      <div className="bg-red-500 transition-all duration-300 ease-out" style={{ width: '40%' }} />
                    </div>

                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-xs font-medium">
                      6 Done • 4 Todo | 10 Total
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Task Timeline */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Task Timeline View</h3>

              <div className="bg-white border rounded-lg overflow-hidden border-gray-200">
                {/* Time slots */}
                <div className="divide-y divide-gray-200">
                  {/* 9:00 AM slot */}
                  <div className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium text-gray-900 w-16">09:00</span>
                        <div className="flex-1">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-blue-900">Morning Setup</h4>
                                <p className="text-sm text-blue-700">Prepare workstation and review daily tasks</p>
                                <div className="flex items-center space-x-2 mt-1">
                                  <UserIcon className="h-4 w-4 text-blue-600" />
                                  <span className="text-xs text-blue-600">John Smith - Manager</span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  In Progress
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 10:00 AM slot */}
                  <div className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium text-gray-900 w-16">10:00</span>
                        <div className="flex-1">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-green-900">Customer Service Training</h4>
                                <p className="text-sm text-green-700">Complete customer service training module</p>
                                <div className="flex items-center space-x-2 mt-1">
                                  <UserIcon className="h-4 w-4 text-green-600" />
                                  <span className="text-xs text-green-600">Sarah Johnson - Staff</span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Completed
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 11:00 AM slot */}
                  <div className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium text-gray-900 w-16">11:00</span>
                        <div className="flex-1">
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-yellow-900">Inventory Check</h4>
                                <p className="text-sm text-yellow-700">Conduct weekly inventory verification</p>
                                <div className="flex items-center space-x-2 mt-1">
                                  <UserIcon className="h-4 w-4 text-yellow-600" />
                                  <span className="text-xs text-yellow-600">Mike Davis - Supervisor</span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Pending
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Task Rearrangement Interface */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Task Rearrangement</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Rearrange Tasks</h2>
                </div>

                <div className="p-6 space-y-4">
                  <div className="text-sm text-gray-600 mb-4">
                    Select a new time for each task. If there's a conflict with the same staff member, you'll be prompted to resolve it.
                  </div>

                  {/* Time slot group */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-gray-800 mb-3">Tasks at 09:00</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">Morning Setup</div>
                          <div className="text-sm text-gray-600">John Smith - Manager</div>
                          <div className="text-xs text-gray-500">Customer Service Process</div>
                        </div>

                        <div className="flex items-center space-x-3">
                          <div className="text-sm text-gray-600">Move to:</div>
                          <select className="border border-gray-300 rounded-md px-3 py-1 text-sm opacity-75 cursor-not-allowed">
                            <option>Select new time</option>
                            <option>08:00</option>
                            <option>10:00</option>
                            <option>11:00</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action button */}
                  <div className="flex justify-center pt-4 border-t">
                    <button className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors shadow-md opacity-75 cursor-not-allowed">
                      Done Rearranging
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Time-Based Scheduling</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Tasks are scheduled throughout the day in hourly time slots from 6 AM to 11 PM,
                  allowing precise planning and execution timing.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Real-Time Progress</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Live progress tracking with visual progress bars and completion statistics
                  that update automatically as tasks are completed.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Conflict Resolution</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Intelligent conflict detection when scheduling tasks for the same staff member,
                  with automatic suggestions for resolution.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">Staff Workload Management</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Monitor staff assignments and prevent over-scheduling with visual indicators
                  and workload balancing capabilities.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Break Management</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Schedule breaks and rest periods throughout the day to maintain staff
                  productivity and prevent burnout.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Bottleneck Analysis</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Identify workflow bottlenecks and scheduling conflicts with visual analysis
                  tools and optimization recommendations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Daily Task Management Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Daily Planning</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Review tasks daily and adjust priorities as needed</li>
                    <li>• Schedule high-priority tasks during peak productivity hours</li>
                    <li>• Include buffer time between tasks for transitions</li>
                    <li>• Balance workload across team members</li>
                    <li>• Plan breaks to maintain staff energy levels</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">Time Management</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Use time slots effectively without over-scheduling</li>
                    <li>• Monitor real-time progress throughout the day</li>
                    <li>• Address bottlenecks as soon as they appear</li>
                    <li>• Adjust schedules dynamically based on actual progress</li>
                    <li>• End-of-day review to improve tomorrow's planning</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Staff Coordination</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Communicate schedule changes immediately</li>
                    <li>• Respect staff availability and preferences</li>
                    <li>• Use rearrangement tools to resolve conflicts quickly</li>
                    <li>• Provide clear task instructions and expectations</li>
                    <li>• Recognize and reward completed work</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-pink-900 mb-3">Performance Optimization</h3>
                  <ul className="space-y-2 text-sm text-pink-700">
                    <li>• Track completion rates and identify improvement areas</li>
                    <li>• Analyze bottleneck patterns for process improvements</li>
                    <li>• Optimize task assignments based on staff strengths</li>
                    <li>• Use progress data to forecast future capacity needs</li>
                    <li>• Continuously refine scheduling processes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Materials & Inventory */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6">
            <div className="flex items-center">
              <CheckBadgeIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Materials & Inventory</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Using Project Materials</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-2">
                <li>The task card includes a "Use Material" button for External projects to record material usage.</li>
                <li>When tapped the app opens a fixed modal overlay (high z-index) — it appears above all content and is mobile-friendly.</li>
                <li>Select a material, enter the quantity (typing is allowed; validation occurs on blur). The input accepts empty typing and will be clamped to available quantity on blur.</li>
                <li>On submit the backend will <strong>accumulate</strong> used quantity (it adds to previous uses) and reduce the available quantity accordingly.</li>
                <li>Materials are stored on the Project as JSON in the `Materials` field. If a project shows invalid materials text like "[object Object]" re-save the project from the editor to fix the stored format.</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-900 mt-4 mb-2">Materials Dashboard & Invoicing</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-2">
                <li>The project list shows a mini stats dashboard for materials with progress bars: used / remaining / total.</li>
                <li>Invoices automatically include only materials where <strong>used === true</strong>.</li>
                <li>Invoice line price uses the material's `unit_cost` (not quantity × unit_cost), matching current billing rules.</li>
                <li>The UI no longer exposes separate "unit" fields — quantities are numeric counts only.</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-900 mt-4 mb-2">Mobile Notes</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-2">
                <li>Modal is responsive with large touch targets and stacks inputs vertically on narrow screens.</li>
                <li>If parsing errors occur, the app logs diagnostics; contact an admin to re-save affected projects if needed.</li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}